// JW Player uploads of the content edit pages (movie, TV show, podcast…).
import { useEffect } from "react";
import {
  completeMultipartUpload,
  getJwMediaInfo,
  getUploadParts,
  jwplayerRequest,
  tryUploadFileToLink,
  uploadFilePart,
} from "../../../services/jwplayer";
import { updateMediaInfo } from "../../../services/content";
import { addGenreTags } from "../../../utils/jwTags";
import { stripHtml } from "../../../utils/list";

const MB = 1024 * 1024;

/** Time JW Player needs to process a new media before its info is read. */
export const PROCESSING_DELAY_MS = 60000;

export const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Chunks of a multipart upload: 10 MB parts under 150 MB, 100 MB parts over
 * 1 GB, 50 MB parts otherwise; files over 4.5 GB use one part per 5 GB.
 */
export function chunkPlan(fileSize) {
  let chunkSize = 50 * MB;
  if (fileSize > 20 * 50 * MB) chunkSize = 100 * MB;
  else if (fileSize < 3 * 50 * MB) chunkSize = 10 * MB;
  let totalChunks = Math.ceil(fileSize / chunkSize);
  if (fileSize > 4500 * MB) {
    totalChunks = Math.floor(fileSize / MB / 1024 / 5) + 1;
    chunkSize = fileSize / totalChunks;
  }
  return { chunkSize, totalChunks };
}

/** JW metadata of a media: title, plain text description, tags and genre tags. */
export function jwMetadata(media, tagType, genres) {
  const metadata = {
    title: media?.title,
    description: stripHtml(media?.description),
    tags: media?.jw_tags,
    custom_params: {},
  };
  addGenreTags(metadata.custom_params, tagType, genres);
  return metadata;
}

/**
 * Creates a JW media and uploads `file` to it in parts, one after the other.
 * `onStart(totalParts)`, `onPart(uploadedParts)` and `onProgress(percent of
 * the current part)` report the progress. Resolves with the JW media id;
 * rejects when a step fails.
 */
export async function uploadMultipart(
  file,
  metadata,
  { onStart = () => {}, onPart = () => {}, onProgress = () => {} } = {}
) {
  const created = await jwplayerRequest({
    body: { upload: { method: "multipart" }, metadata },
    url: "/media/",
    method: "post",
    jw_tag: true,
  });
  const { id, upload_id, upload_token } = created.data.data;
  const { chunkSize, totalChunks } = chunkPlan(file.size);
  onStart(totalChunks);

  const parts = await getUploadParts(upload_token, upload_id, totalChunks);
  if (!parts) throw new Error("Could not get the upload parts");
  for (let i = 0; i < totalChunks; i++) {
    const chunk = file.slice(i * chunkSize, Math.min((i + 1) * chunkSize, file.size));
    await uploadFilePart(parts[i].upload_link, chunk, onProgress);
    onPart(i + 1);
  }
  const completed = await completeMultipartUpload(upload_id, upload_token);
  if (completed instanceof Error) throw completed;
  return id;
}

/**
 * Duration (seconds) of a JW media once processed, or null while JW is still
 * processing it (long videos can take many minutes) or on error.
 */
export async function jwDuration(mediaId) {
  try {
    const response = await getJwMediaInfo(mediaId);
    const { status, duration } = response.data || {};
    const ready = !status || status === "ready";
    return response.status === 200 && ready && duration > 0 ? duration : null;
  } catch (error) {
    console.error("Error while getting media info:", error);
    return null;
  }
}

/** Interval between two checks of a media being processed by JW. */
export const PROCESSING_CHECK_MS = 20000;

/**
 * Checks every `interval` ms until JW knows the duration of a media. Resolves
 * with the duration, or null after `timeout` ms or once `isCancelled()`.
 */
export async function waitForJwDuration(
  mediaId,
  { interval = PROCESSING_CHECK_MS, timeout = PROCESSING_DELAY_MS, isCancelled = () => false } = {}
) {
  const end = Date.now() + timeout;
  while (!isCancelled()) {
    const duration = await jwDuration(mediaId);
    if (duration) return duration;
    if (Date.now() + interval > end) return null;
    await delay(interval);
  }
  return null;
}

/**
 * Stores the JW duration of a media saved without one (missing, 0 or not yet
 * known by JW at upload time) with `save(duration)` (movie media by default).
 * Resolves with the save response, or null.
 */
export async function repairMediaDuration(
  media,
  save = (duration) => updateMediaInfo(media._id, { duration })
) {
  if (!media?.media_id || media.duration > 0) return null;
  const duration = await jwDuration(media.media_id);
  return duration ? save(duration) : null;
}

/** How long an edit page keeps checking JW for the duration of a long video. */
export const DURATION_CHECK_MS = 3 * 60 * 60 * 1000;

/**
 * While a page is open, keeps checking JW until it knows the real duration
 * of a linked video that has none yet (or whose id is `unverifiedId`, i.e.
 * saved with the local file duration), then calls `save(duration)`.
 * Returns whether the check is running.
 */
export function useDurationCheck(mediaId, duration, unverifiedId, save) {
  const checking =
    Boolean(mediaId) && (!(duration > 0) || mediaId === unverifiedId);
  useEffect(() => {
    if (!checking) return undefined;
    let cancelled = false;
    waitForJwDuration(mediaId, {
      timeout: DURATION_CHECK_MS,
      isCancelled: () => cancelled,
    })
      .then((jwDuration) => !cancelled && jwDuration && save(jwDuration))
      .catch((error) => console.error("Error while checking duration:", error));
    return () => {
      cancelled = true;
    };
    // `save` is a new function on each render; the check depends on the media only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checking, mediaId]);
  return checking;
}

/**
 * Creates a JW media (trailer, episode) and uploads `file` in one request.
 * Resolves with the JW media id, or null when the upload failed.
 */
export async function uploadDirect(
  file,
  metadata,
  { mimeType = "video/mp4", jwTag = false } = {}
) {
  const created = await jwplayerRequest({
    body: { upload: { method: "direct", mime_type: mimeType }, metadata },
    url: "/media/",
    method: "post",
    jw_tag: jwTag,
  });
  const { id, upload_link } = created.data.data;
  const uploaded = await tryUploadFileToLink(upload_link, file, file.type);
  return uploaded ? id : null;
}
